import React, { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { api, errMsg, formatPrice } from "../lib/api";

const STATUS_LABEL = {
  paid: "Order received",
  packed: "Packed",
  shipped: "Shipped",
  out_for_delivery: "Out for delivery",
  delivered: "Delivered",
  cancelled: "Cancelled",
};
const STEPS = ["paid", "packed", "shipped", "out_for_delivery", "delivered"];

export default function TrackOrder() {
  const [params] = useSearchParams();
  const [orderNumber, setOrderNumber] = useState(params.get("order") || "");
  const [email, setEmail] = useState(params.get("email") || "");
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setError(""); setData(null);
    try {
      const { data } = await api.get(`/track/${orderNumber}?email=${encodeURIComponent(email)}`);
      setData(data);
    } catch (e) {
      setError(errMsg(e, "Order not found"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-[900px] mx-auto px-6 py-16">
      <div className="text-[11px] tracking-[0.25em] uppercase text-[#B026FF] font-bold">Track</div>
      <h1 className="mt-2 font-display font-extrabold text-4xl lg:text-5xl tracking-tighter text-white">Track your order</h1>
      <p className="mt-3 text-white/60 max-w-md">Enter your order number and email — we'll show you exactly where your pulls are.</p>

      <form onSubmit={submit} className="mt-8 card-elev rounded-xl p-6 grid grid-cols-1 md:grid-cols-3 gap-4 items-end">
        <label className="md:col-span-1">
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">Order number</div>
          <input value={orderNumber} onChange={(e) => setOrderNumber(e.target.value)} required data-testid="track-order-input"
            placeholder="KC-XXXXXX-XXXXXX" className={inputCls} />
        </label>
        <label className="md:col-span-1">
          <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold mb-2">Email</div>
          <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="track-email-input"
            placeholder="you@example.com" className={inputCls} />
        </label>
        <button disabled={busy} data-testid="track-submit" className="btn-violet h-11 rounded-md text-sm md:col-span-1">
          {busy ? "Looking up…" : "Track order"}
        </button>
      </form>

      {error && <div className="mt-6 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-md px-3 py-2">{error}</div>}

      {data && (
        <div className="mt-10 card-elev rounded-xl p-8" data-testid="track-results">
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold">Order</div>
              <div className="font-mono text-2xl text-white">{data.order_number}</div>
            </div>
            <div className="text-right">
              <div className="text-[11px] tracking-[0.22em] uppercase text-white/50 font-bold">Status</div>
              <div className="text-[#B026FF] uppercase tracking-wider font-bold">{STATUS_LABEL[data.status] || data.status}</div>
            </div>
          </div>

          {/* Progress */}
          <div className="mt-8">
            {data.status === "cancelled" ? (
              <div className="text-red-300">This order was cancelled.</div>
            ) : (
              <div className="flex items-center justify-between">
                {STEPS.map((s, i) => {
                  const currentIdx = STEPS.indexOf(data.status);
                  const done = i <= currentIdx;
                  const isCurrent = i === currentIdx;
                  return (
                    <React.Fragment key={s}>
                      <div className="flex flex-col items-center text-center min-w-0">
                        <div className={`w-9 h-9 rounded-full flex items-center justify-center font-mono text-sm border-2 transition ${
                          done ? "bg-[#B026FF] border-[#B026FF] text-white" : "border-white/20 text-white/40"
                        } ${isCurrent ? "ring-4 ring-[#B026FF]/30" : ""}`}>
                          {i + 1}
                        </div>
                        <div className={`mt-2 text-[10px] tracking-[0.18em] uppercase font-bold ${done ? "text-white" : "text-white/40"}`}>
                          {STATUS_LABEL[s]}
                        </div>
                      </div>
                      {i < STEPS.length - 1 && (
                        <div className={`flex-1 h-0.5 mx-1 ${i < currentIdx ? "bg-[#B026FF]" : "bg-white/10"}`} />
                      )}
                    </React.Fragment>
                  );
                })}
              </div>
            )}
          </div>

          {data.tracking_number && (
            <div className="mt-8 p-4 rounded-md bg-[#B026FF]/10 border border-[#B026FF]/30">
              <div className="text-[11px] tracking-[0.22em] uppercase text-[#B026FF] font-bold">Tracking number</div>
              <div className="mt-1 font-mono text-white text-lg">{data.tracking_number}</div>
            </div>
          )}

          <div className="mt-8">
            <h3 className="font-display text-xl text-white">Items</h3>
            <ul className="mt-4 divide-y divide-white/5">
              {data.items.map((it) => (
                <li key={it.product_id} className="py-3 flex items-center justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="text-white truncate">{it.name}</div>
                    <div className="text-xs text-white/50 font-mono">× {it.quantity}</div>
                  </div>
                  <div className="font-mono text-white">{formatPrice(it.line_total_cents)}</div>
                </li>
              ))}
            </ul>
            <div className="mt-4 flex justify-between font-mono text-white border-t border-white/5 pt-3">
              <span>Total</span>
              <span>{formatPrice(data.total_cents)}</span>
            </div>
          </div>

          <div className="mt-8">
            <h3 className="font-display text-xl text-white">History</h3>
            <ol className="mt-4 space-y-3">
              {(data.status_history || []).map((h, i) => (
                <li key={i} className="flex items-start gap-4">
                  <div className="w-2 h-2 rounded-full bg-[#B026FF] mt-2" />
                  <div>
                    <div className="text-sm text-white uppercase tracking-wider font-bold">{(h.status || "").replace(/_/g, ' ')}</div>
                    <div className="text-xs text-white/50 font-mono">{new Date(h.at).toLocaleString()}</div>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      )}
    </div>
  );
}

const inputCls = "w-full bg-[#110E1D] border border-white/10 rounded-md px-3 py-2.5 text-sm text-white outline-none focus:border-[#B026FF]/60";
